import { useState } from 'react';
import { format } from 'date-fns';
import { ChevronDownIcon, ChevronUpIcon, UserIcon, TableCellsIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../context/ThemeContext';
import { formatCurrency } from '../config/currency';
import OrderLineTimeline from './OrderLineTimeline';

interface OrderLineData {
  sessionCode: string;
  sessionId: number;
  status: string;
  startedAt: string;
  totalAmount: number;
  paidAmount: number;
  remainingAmount: number;
  qrCode: string;
  location: string;
  customerIdentifier: string;
  customerType: string;
  orders: Array<{ id: number; customOrderId: string; status: string; createdAt: string; items: Array<{ name: string; quantity: number; price: number; }>; }>;
}

interface OrderLineCardProps {
  orderLine: OrderLineData;
  onOrderClick?: (orderId: number) => void;
  onStatusUpdate?: (sessionId: number, newStatus: string) => void;
  showActions?: boolean;
}

const OrderLineCard: React.FC<OrderLineCardProps> = ({ 
  orderLine, 
  onOrderClick, 
  onStatusUpdate,
  showActions = true 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Transform orders data to match OrderLineTimeline interface
  const transformedOrders = orderLine.orders?.map(order => ({
    id: order.id,
    orderId: order.id,
    status: order.status,
    total: order.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0,
    createdAt: order.createdAt,
    itemCount: order.items?.reduce((sum, item) => sum + item.quantity, 0) || 0,
    items: order.items || []
  })) || [];

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'BILL_REQUESTED':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'PARTIALLY_PAID':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'PAID':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'CLOSED':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getCustomerDisplay = () => {
    if (orderLine.customerType === 'GUEST') {
      return orderLine.customerIdentifier; // Phone number for guests
    } else if (orderLine.customerType === 'STEWARD') {
      return 'Steward Order';
    } else {
      return `User #${orderLine.customerIdentifier}`;
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="p-3 sm:p-4">
        <div className="flex items-start justify-between mb-3 gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-2 sm:space-y-0">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                {orderLine.location} - {orderLine.qrCode}
              </h3>
              <span className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(orderLine.status)} self-start`}>
                {orderLine.status.replace('_', ' ')}
              </span>
            </div>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex-shrink-0 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors bg-white/20 backdrop-blur-sm"
          >
            {isExpanded ? (
              <ChevronUpIcon className="w-5 h-5 text-white" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-white" />
            )}
          </button>
        </div>

        {/* Summary Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="flex items-center space-x-2 min-w-0">
            <UserIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-500 dark:text-gray-400">Customer</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {getCustomerDisplay()}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2 min-w-0">
            <TableCellsIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-500 dark:text-gray-400">Session Code</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {orderLine.sessionCode}
              </p>
            </div>
          </div>

          {/* Only show Total/Paid if the data makes sense */}
          {(orderLine.status !== 'PAID' || orderLine.totalAmount === orderLine.paidAmount) && (
            <div className="flex items-center space-x-2 min-w-0">
              <CurrencyDollarIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-500 dark:text-gray-400">Total / Paid</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {formatCurrency(orderLine.totalAmount)} / {formatCurrency(orderLine.paidAmount)}
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center space-x-2 min-w-0">
            <div className="w-4 h-4 flex-shrink-0"></div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-500 dark:text-gray-400">Started</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {format(new Date(orderLine.startedAt), 'HH:mm')}
              </p>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="mt-3 flex flex-col space-y-1 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 text-sm">
          <span className="text-gray-600 dark:text-gray-400">
            {orderLine.orders?.length || 0} order{(orderLine.orders?.length || 0) !== 1 ? 's' : ''} placed
          </span>
          {orderLine.remainingAmount > 0 && orderLine.status !== 'PAID' && (
            <span className="text-orange-600 dark:text-orange-400 font-medium">
              {formatCurrency(orderLine.remainingAmount)} remaining
            </span>
          )}
        </div>
      </div>

      {/* Expandable Timeline */}
      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-3 sm:p-4 bg-gray-50 dark:bg-gray-900">
          {/* Consolidated Items */}
          {(() => {
            // Consolidate all items across all orders
            const itemsMap = new Map<string, { name: string; quantity: number; price: number; total: number }>();
            
            orderLine.orders?.forEach(order => {
              order.items?.forEach(item => {
                const key = `${item.name}-${item.price}`;
                if (itemsMap.has(key)) {
                  const existing = itemsMap.get(key)!;
                  existing.quantity += item.quantity;
                  existing.total = existing.quantity * existing.price;
                } else {
                  itemsMap.set(key, {
                    name: item.name,
                    quantity: item.quantity,
                    price: item.price,
                    total: item.quantity * item.price
                  });
                }
              });
            });
            
            const consolidatedItems = Array.from(itemsMap.values());
            
            return consolidatedItems.length > 0 ? (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Items Ordered
                </h4>
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Item
                        </th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Qty
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Price
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {consolidatedItems.map((item, index) => (
                        <tr key={index}>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                            {item.name}
                          </td>
                          <td className="px-3 py-2 text-sm text-center text-gray-900 dark:text-gray-100">
                            {item.quantity}
                          </td>
                          <td className="px-3 py-2 text-sm text-right text-gray-900 dark:text-gray-100">
                            {formatCurrency(item.price)}
                          </td>
                          <td className="px-3 py-2 text-sm text-right font-medium text-gray-900 dark:text-gray-100">
                            {formatCurrency(item.total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <td colSpan={3} className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 text-right">
                          Session Total:
                        </td>
                        <td className="px-3 py-2 text-sm font-bold text-gray-900 dark:text-gray-100 text-right">
                          {formatCurrency(orderLine.totalAmount)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            ) : null;
          })()}
          
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Order Timeline
          </h4>
          <OrderLineTimeline orders={transformedOrders} onOrderClick={onOrderClick} />
          
          {/* Actions */}
          {showActions && orderLine.status === 'ACTIVE' && (
            <div className="mt-6">
              <div className="flex flex-col space-y-2 sm:flex-row sm:justify-end sm:space-y-0 sm:space-x-3">
                <button
                  onClick={() => onStatusUpdate?.(orderLine.sessionId, 'BILL_REQUESTED')}
                  className="w-full sm:w-auto px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm font-medium"
                >
                  Request Bill
                </button>
                <button
                  onClick={() => onStatusUpdate?.(orderLine.sessionId, 'CLOSED')}
                  className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                >
                  Close Session
                </button>
                <button
                  onClick={() => onStatusUpdate?.(orderLine.sessionId, 'CANCELLED')}
                  className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  Cancel Session
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderLineCard;